import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, MenuItem, Stack, Switch, TextField, Tooltip, Typography, createFilterOptions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useKatalogQuery, useUlozZarizeniMutation, naPozadavek } from '../../api/JsdhNotifikaceService/service/IotZarizeniMutations';
import {
    KATEGORIE, PODPORA_OVLADANI, POVOLIT_PRIDANI_SIA, POVOLIT_PRIDANI_ZIGBEE, ovladateleKomponenty, vychoziNazevKanalu, type IotZarizeni, type IotZarizeniRequest, type KatalogModel, type Protokol,
} from '../../api/JsdhNotifikaceService/service/IotZarizeniService';
import { TOOLTIP_NEOVERENY_MODEL } from './iotStav';

// Segmenty MQTT topicu, ktere gateway i BE odmitaji jako ID zarizeni (bez ohledu na velikost pismen).
export const REZERVOVANE_SEGMENTY = ['events', 'status', 'online', 'rpc', 'command'];
const PROFILY: Record<string, string> = { switch: 'Spínače (2 výstupy)', cover: 'Roleta / motor' };
const PROTOKOLY: Record<Protokol, string> = { SHELLY_MQTT: 'Shelly MQTT', ZIGBEE: 'Zigbee', SIA: 'SIA (EPS/EZS)' };
const LABEL_ID: Record<Protokol, string> = {
    SHELLY_MQTT: 'ID zařízení', ZIGBEE: 'IEEE adresa nebo název v Zigbee', SIA: 'SIA account',
};
const RUCNI_KANALY: Record<string, string[]> = {
    ZASUVKA: ['switch:0'], MODUL_1K: ['switch:0'], STMIVAC: ['light:0'], MODUL_2K: ['switch:0', 'switch:1'],
};

/** Ovládané kanály při založení (spec katalogu 4.2): z modelu a profilu, u ručního modelu podle kategorie. */
export const kanalyProZalozeni = (model: KatalogModel | undefined, profil: string, kategorie: string | null | undefined): string[] =>
    model ? ovladateleKomponenty(model, profil) : RUCNI_KANALY[kategorie ?? ''] ?? [];

/** Stejná pravidla jako BE (IotZarizeniService): klíč pole -> česká chyba. */
const dnesLokalne = (): string => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const validujZarizeni = (f: IotZarizeniRequest, dnes = dnesLokalne(), vyzadujeProfil = false, profil = ''): Record<string, string> => {
    const e: Record<string, string> = {};
    const id = (f.identifikator ?? '').trim();
    if (f.protokol === 'SHELLY_MQTT' && (!/^[A-Za-z0-9_-]{1,128}$/.test(id) || REZERVOVANE_SEGMENTY.includes(id.toLowerCase()))) {
        e.identifikator = 'ID smí obsahovat jen písmena bez diakritiky, číslice, - a _ a nesmí být events, status, online, rpc ani command';
    } else if (f.protokol === 'ZIGBEE' && !/^[^/+#]{1,128}$/.test(id)) {
        e.identifikator = 'Neplatná IEEE adresa nebo název';
    } else if (f.protokol === 'SIA' && !/^[0-9A-Za-z-]{1,16}$/.test(id)) {
        e.identifikator = 'Neplatný SIA account';
    }
    if (!f.nazev?.trim() || f.nazev.trim().length > 100) e.nazev = 'Název 1 až 100 znaků';
    if ((f.umisteni ?? '').length > 200) e.umisteni = 'Nejvýš 200 znaků';
    if ((f.vyrobniCislo ?? '').length > 64) e.vyrobniCislo = 'Nejvýš 64 znaků';
    if (f.datumInstalace && f.datumInstalace > dnes) e.datumInstalace = 'Datum nesmí být v budoucnosti';
    if ((f.vyrobce ?? '').length > 64) e.vyrobce = 'Nejvýš 64 znaků';
    if ((f.modelNazev ?? '').length > 128) e.modelNazev = 'Nejvýš 128 znaků';
    if ((f.modelKod ?? '').length > 32) e.modelKod = 'Nejvýš 32 znaků';
    if ((f.poznamka ?? '').length > 500) e.poznamka = 'Nejvýš 500 znaků';
    if (vyzadujeProfil && !profil) e.profil = 'Vyberte profil zařízení';
    if (f.protokol !== 'SIA' && !f.modelKod && !f.kategorie) e.kategorie = 'Vyberte kategorii';
    return e;
};

export interface ZarizeniDialogProps {
    objektId: number;
    /** Název objektu pro nadpis „Přidat zařízení do objektu …“. */
    objektNazev?: string;
    zarizeni?: IotZarizeni;
    predvyplneni?: Partial<IotZarizeniRequest>;
    /**
     * Přidání z Evidence (spec UX IoT 4.8): objekty jednotky pro pole Objekt. U jediného objektu je předvybraný
     * a pole se nezobrazuje; bez seznamu se pole nezobrazuje vůbec (přidání z detailu objektu).
     */
    objekty?: { id: number; nazev: string }[];
    onClose: () => void;
}

const NAPOVEDA_ID_NOVE = 'Najdete ho v seznamu Nová zařízení v Evidenci, jakmile se zařízení připojí k serveru.';
const NAPOVEDA_ID_UPRAVA = 'ID nelze změnit. Pokud se zařízení vyměnilo, odeberte ho a přidejte nové.';
const INFO_RUCNI_MODEL = 'Ruční model nemáme ověřený. Výstupy se založí podle kategorie, zkontrolujte je po uložení.';
const CHYBA_ULOZENI = 'Zařízení se nepodařilo uložit. Zkontrolujte údaje a zkuste to znovu.';
const CHYBA_MODEL = 'Vyberte model, nebo zvolte Model není v seznamu';
/** Obecné texty z handleRequest, když BE žádný vlastní text neposlal. */
const OBECNE_CHYBY = new Set(['', 'Vnitřní chyba serveru.']);

/** Pořadí polí ve formuláři: po neúspěšném odeslání dostane fokus první chybné. */
const PORADI_POLI = ['objekt', 'model', 'kategorie', 'vyrobce', 'modelNazev', 'profil', 'identifikator', 'nazev', 'umisteni',
    'vyrobniCislo', 'datumInstalace', 'poznamka'];
const POLE_DALSI_UDAJE = new Set(['vyrobniCislo', 'datumInstalace', 'poznamka']);

const filtrModelu = createFilterOptions<KatalogModel>({ stringify: (m) => `${m.vyrobce} ${m.nazev} ${m.kod}` });
const JEN_SHELLY = 'Shelly';
/** Protokoly, které jde u nového zařízení zvolit; Zigbee a SIA jsou dočasně skryté za příznaky (HO-163, HO-164). */
const povoleneProtokoly = (): Protokol[] => ['SHELLY_MQTT', ...(POVOLIT_PRIDANI_ZIGBEE ? ['ZIGBEE' as const] : []), ...(POVOLIT_PRIDANI_SIA ? ['SIA' as const] : [])];
const kategorieMimoShelly = (): Set<string> => new Set([...(POVOLIT_PRIDANI_ZIGBEE ? [] : ['ZIGBEE_SENZOR']), ...(POVOLIT_PRIDANI_SIA ? [] : ['SIA_USTREDNA'])]);
const nazevKategorie = (k: string) => KATEGORIE[k] ?? k;

export const nadpisDialogu = (zarizeni?: IotZarizeni, objektNazev?: string): string =>
    zarizeni ? `Upravit zařízení: ${zarizeni.nazev}` : objektNazev ? `Přidat zařízení do objektu ${objektNazev}` : 'Přidat zařízení';

const ZarizeniFormular: React.FC<ZarizeniDialogProps> = ({ objektId, objektNazev, zarizeni, predvyplneni, objekty, onClose }) => {
    const { data: katalog = [] } = useKatalogQuery();
    const ulozit = useUlozZarizeniMutation();
    const [chyba, setChyba] = useState<string | null>(null);
    const [f, setF] = useState<IotZarizeniRequest>(() => zarizeni ? naPozadavek(zarizeni) : {
        objektId, protokol: 'SHELLY_MQTT', identifikator: '', nazev: '', aktivni: true, modelKod: null, kategorie: null, ...predvyplneni,
        // Nepovolený protokol (Zigbee, SIA) se u nového zařízení přebije na Shelly MQTT (BE ostatní zatím odmítne).
        ...(predvyplneni?.protokol && !povoleneProtokoly().includes(predvyplneni.protokol) ? { protokol: 'SHELLY_MQTT' as const } : {}),
    });
    const model = katalog.find((m) => m.kod === f.modelKod);
    // Ruční zadání: zařízení bez modelu z katalogu (úprava), předvyplněná kategorie bez modelu (čidlo, ústředna
    // z evidence), nebo katalog není k dispozici (chyba načtení) - review K6-K9, Q3.
    const [rucne, setRucne] = useState<boolean>(() => !model && (!!zarizeni || !!f.kategorie || katalog.length === 0));
    const [profil, setProfil] = useState<string>('');
    const [dotceno, setDotceno] = useState<Record<string, boolean>>({});
    const [odeslano, setOdeslano] = useState(false);
    const [dalsiUdaje, setDalsiUdaje] = useState(false);
    const [fokus, setFokus] = useState<string | null>(null);
    const pole = useRef<Record<string, HTMLInputElement | null>>({});
    const ref = (klic: string) => (el: HTMLInputElement | null) => { pole.current[klic] = el; };

    // Nové Shelly zařízení má výrobce pevně Shelly (BE cizího výrobce u SHELLY_MQTT odmítne); úprava beze změny.
    const jenShelly = !zarizeni && f.protokol === 'SHELLY_MQTT';
    const modely = useMemo(() => [...katalog].filter((m) => zarizeni || (m.protokol === 'SHELLY_MQTT' ? m.vyrobce === JEN_SHELLY : povoleneProtokoly().includes(m.protokol)))
        .sort((a, b) => nazevKategorie(a.kategorie).localeCompare(nazevKategorie(b.kategorie), 'cs')
        || `${a.vyrobce} ${a.nazev}`.localeCompare(`${b.vyrobce} ${b.nazev}`, 'cs')), [katalog, zarizeni]);
    const profily = Array.from(new Set((model?.komponenty ?? []).map((k) => k.profil).filter(Boolean)));
    const chyby = validujZarizeni(f, dnesLokalne(), !zarizeni && f.protokol === 'SHELLY_MQTT' && profily.length > 0, profil);
    // Chybějící model se v režimu katalogu hlásí u pole Model, v ručním režimu u Kategorie.
    const chybyPoli: Record<string, string> = { ...chyby };
    const vyberObjektu = !zarizeni && !!objekty && objekty.length > 1;
    if (vyberObjektu && !f.objektId) chybyPoli.objekt = 'Vyberte objekt';
    if (chyby.kategorie && !rucne) {
        delete chybyPoli.kategorie;
        chybyPoli.model = CHYBA_MODEL;
    }
    const ukaz = (klic: string): string | undefined => ((odeslano || dotceno[klic]) ? chybyPoli[klic] : undefined);
    const opusteno = (klic: string) => () => setDotceno((d) => (d[klic] ? d : { ...d, [klic]: true }));

    useEffect(() => {
        if (!fokus) return undefined;
        // Sekce Další údaje se po rozbalení teprve animuje, proto fokus až v dalším kroku.
        const t = window.setTimeout(() => { pole.current[fokus]?.focus(); setFokus(null); }, 0);
        return () => window.clearTimeout(t);
    }, [fokus]);

    const nastavModel = (m: KatalogModel | null) => {
        if (!m) {
            // Nové zařízení: protokol (a s ním popisek ID) se vrátí na výchozí, ne na protokol smazaného modelu (Q4).
            setF({ ...f, modelKod: null, kategorie: null, vyrobce: null, modelNazev: null,
                protokol: zarizeni ? f.protokol : predvyplneni?.protokol && povoleneProtokoly().includes(predvyplneni.protokol) ? predvyplneni.protokol : 'SHELLY_MQTT' });
        } else {
            setF({ ...f, modelKod: m.kod, kategorie: m.kategorie, vyrobce: m.vyrobce, modelNazev: m.nazev,
                // Při úpravě je protokol jen pro čtení (identita zařízení se nemění).
                protokol: zarizeni ? f.protokol : m.protokol });
        }
        setProfil('');
    };
    const naRucni = () => { setRucne(true); setF({ ...f, modelKod: null, ...(jenShelly ? { vyrobce: JEN_SHELLY } : {}) }); setProfil(''); };
    const naKatalog = () => { setRucne(false); setF({ ...f, modelKod: null, kategorie: null, vyrobce: null, modelNazev: null }); };

    const odeslat = () => {
        setOdeslano(true);
        const prvni = PORADI_POLI.find((k) => chybyPoli[k]);
        if (prvni) {
            if (POLE_DALSI_UDAJE.has(prvni)) setDalsiUdaje(true);
            setFokus(prvni);
            return;
        }
        const data: IotZarizeniRequest = { ...f, identifikator: f.identifikator.trim() };
        if (jenShelly && !f.modelKod) data.vyrobce = JEN_SHELLY;
        if (f.protokol === 'SIA' && !f.kategorie && !f.modelKod) data.kategorie = 'SIA_USTREDNA';
        if (!zarizeni && f.protokol === 'SHELLY_MQTT') {
            data.kanaly = kanalyProZalozeni(model, profil, f.kategorie).map((komponenta) => ({
                komponenta, nazev: vychoziNazevKanalu(komponenta),
            }));
        }
        setChyba(null);
        ulozit.mutate({ id: zarizeni?.id, data }, {
            onSuccess: onClose,
            onError: (e: Error) => setChyba(OBECNE_CHYBY.has((e?.message ?? '').trim()) ? CHYBA_ULOZENI : e.message),
        });
    };

    const napovedaId = zarizeni ? NAPOVEDA_ID_UPRAVA : f.protokol !== 'SIA' ? NAPOVEDA_ID_NOVE : undefined;

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{nadpisDialogu(zarizeni, objektNazev)}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {!zarizeni && <Alert severity="info" variant="standard">{PODPORA_OVLADANI}</Alert>}
                    {chyba && <Alert severity="error">{chyba}</Alert>}
                    {vyberObjektu && (
                        <TextField select label="Objekt" required value={f.objektId || ''} inputRef={ref('objekt')}
                            onBlur={opusteno('objekt')} error={!!ukaz('objekt')} helperText={ukaz('objekt')}
                            onChange={(e) => setF({ ...f, objektId: Number(e.target.value) })}>
                            {objekty!.map((o) => <MenuItem key={o.id} value={o.id}>{o.nazev}</MenuItem>)}
                        </TextField>
                    )}
                    {!rucne ? (
                        <Box>
                            <Autocomplete
                                options={modely}
                                value={model ?? null}
                                onChange={(_, m) => nastavModel(m)}
                                groupBy={(m) => nazevKategorie(m.kategorie)}
                                getOptionLabel={(m) => `${m.vyrobce} ${m.nazev}`}
                                isOptionEqualToValue={(a, b) => a.kod === b.kod}
                                filterOptions={filtrModelu}
                                noOptionsText="Žádný model neodpovídá"
                                renderInput={(params) => (
                                    <TextField {...params} label="Model" required placeholder="Hledat model, např. 2PM" inputRef={ref('model')}
                                        onBlur={opusteno('model')} error={!!ukaz('model')} helperText={ukaz('model')} />
                                )}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                <Button size="small" onClick={naRucni}>Model není v seznamu</Button>
                                {model && !model.overeno && (
                                    <Tooltip title={TOOLTIP_NEOVERENY_MODEL}>
                                        <Chip size="small" variant="outlined" label="Neověřený model" />
                                    </Tooltip>
                                )}
                                {model && <Typography variant="body2" color="text.secondary">{nazevKategorie(model.kategorie)}</Typography>}
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <TextField select label="Kategorie" required value={f.kategorie ?? ''} inputRef={ref('kategorie')}
                                onBlur={opusteno('kategorie')} error={!!ukaz('kategorie')} helperText={ukaz('kategorie')}
                                onChange={(e) => setF({ ...f, kategorie: e.target.value, modelKod: null })}>
                                {Object.entries(KATEGORIE).filter(([k]) => !!zarizeni || !kategorieMimoShelly().has(k)).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                            </TextField>
                            <TextField label="Výrobce" value={jenShelly ? JEN_SHELLY : f.vyrobce ?? ''} inputRef={ref('vyrobce')} onBlur={opusteno('vyrobce')}
                                error={!!ukaz('vyrobce')} helperText={ukaz('vyrobce')}
                                InputProps={{ readOnly: jenShelly }} onChange={(e) => setF({ ...f, vyrobce: e.target.value })} />
                            <TextField label="Model" value={f.modelNazev ?? ''} inputRef={ref('modelNazev')} onBlur={opusteno('modelNazev')}
                                error={!!ukaz('modelNazev')} helperText={ukaz('modelNazev')} onChange={(e) => setF({ ...f, modelNazev: e.target.value })} />
                            {!zarizeni && povoleneProtokoly().length > 1 && (
                                <TextField select label="Protokol" value={f.protokol} onChange={(e) => {
                                    const protokol = e.target.value as Protokol;
                                    setF({ ...f, protokol, ...(f.protokol === 'SHELLY_MQTT' && f.vyrobce === JEN_SHELLY ? { vyrobce: null } : {}) });
                                }}>
                                    {povoleneProtokoly().map((p) => <MenuItem key={p} value={p}>{PROTOKOLY[p]}</MenuItem>)}
                                </TextField>
                            )}
                            <Alert severity="info" action={katalog.length > 0
                                ? <Button color="inherit" size="small" onClick={naKatalog}>Vybrat z katalogu</Button> : undefined}>
                                {INFO_RUCNI_MODEL}
                            </Alert>
                        </>
                    )}
                    {!zarizeni && profily.length > 0 && (
                        <TextField select label="Profil zařízení" required value={profil} inputRef={ref('profil')} onBlur={opusteno('profil')}
                            error={!!ukaz('profil')} onChange={(e) => setProfil(e.target.value)}
                            helperText={ukaz('profil') ?? 'Profil se nastavuje v zařízení; gateway ho nikdy nepřepíná.'}>
                            {profily.map((p) => <MenuItem key={p} value={p}>{PROFILY[p] ?? p}</MenuItem>)}
                        </TextField>
                    )}
                    {zarizeni && (
                        <TextField label="Protokol" value={PROTOKOLY[f.protokol] ?? f.protokol} disabled />
                    )}
                    <TextField label={LABEL_ID[f.protokol]} required value={f.identifikator} disabled={!!zarizeni} inputRef={ref('identifikator')}
                        onBlur={opusteno('identifikator')} onChange={(e) => setF({ ...f, identifikator: e.target.value })}
                        error={!!ukaz('identifikator')} helperText={ukaz('identifikator') ?? napovedaId} />
                    <TextField label="Název" required value={f.nazev} inputRef={ref('nazev')} onBlur={opusteno('nazev')}
                        onChange={(e) => setF({ ...f, nazev: e.target.value })} error={!!ukaz('nazev')} helperText={ukaz('nazev')} />
                    <TextField label="Umístění" value={f.umisteni ?? ''} inputRef={ref('umisteni')} onBlur={opusteno('umisteni')}
                        onChange={(e) => setF({ ...f, umisteni: e.target.value })} error={!!ukaz('umisteni')} helperText={ukaz('umisteni')} />
                    <Accordion disableGutters elevation={0} variant="outlined" expanded={dalsiUdaje} onChange={(_, v) => setDalsiUdaje(v)}
                        sx={{ '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2">Další údaje (výrobní číslo, datum instalace, poznámka)</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={2}>
                                <TextField label="Výrobní číslo" value={f.vyrobniCislo ?? ''} inputRef={ref('vyrobniCislo')} onBlur={opusteno('vyrobniCislo')}
                                    onChange={(e) => setF({ ...f, vyrobniCislo: e.target.value })}
                                    error={!!ukaz('vyrobniCislo')} helperText={ukaz('vyrobniCislo')} />
                                <TextField label="Datum instalace" type="date" InputLabelProps={{ shrink: true }} value={f.datumInstalace ?? ''}
                                    inputProps={{ max: dnesLokalne() }} inputRef={ref('datumInstalace')} onBlur={opusteno('datumInstalace')}
                                    onChange={(e) => setF({ ...f, datumInstalace: e.target.value || null })}
                                    error={!!ukaz('datumInstalace')} helperText={ukaz('datumInstalace')} />
                                <TextField label="Poznámka" value={f.poznamka ?? ''} multiline minRows={2} inputRef={ref('poznamka')}
                                    onBlur={opusteno('poznamka')} onChange={(e) => setF({ ...f, poznamka: e.target.value })}
                                    error={!!ukaz('poznamka')} helperText={ukaz('poznamka')} />
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                    <FormControlLabel control={<Switch checked={f.aktivni} onChange={(e) => setF({ ...f, aktivni: e.target.checked })} />} label="Aktivní" />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Zrušit</Button>
                <Button variant="contained" disabled={ulozit.isPending} onClick={odeslat}>{zarizeni ? 'Uložit změny' : 'Přidat'}</Button>
            </DialogActions>
        </Dialog>
    );
};

// Model se z katalogu dohledává jednou při prvním renderu formuláře (ruční / katalogový režim), proto formulář
// vznikne až po načtení katalogu. Při chybě načtení katalogu se formulář otevře bez něj (ruční zadání).
const ZarizeniDialog: React.FC<ZarizeniDialogProps> = (props) => {
    const { isLoading } = useKatalogQuery();
    if (isLoading) {
        return (
            <Dialog open onClose={props.onClose} fullWidth maxWidth="sm">
                <DialogTitle>{nadpisDialogu(props.zarizeni, props.objektNazev)}</DialogTitle>
                <DialogContent>Načítám katalog zařízení…</DialogContent>
            </Dialog>
        );
    }
    return <ZarizeniFormular {...props} />;
};

export default ZarizeniDialog;
